import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Modal, Space, Typography } from '@pankod/refine-antd'
import { supabaseClient } from 'utility/supabaseClient'
import { FRONT_MAIN_URL } from 'utility/constants'

type Mode = 'temporary' | 'stores'
type Status = { mode: Mode; revision: number; 'can_change': boolean }
const labels: Record<Mode, string> = { temporary: 'Home temporal', stores: 'Home con tiendas' }
async function bounded<T> (request: PromiseLike<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([Promise.resolve(request), new Promise<T>((resolve, reject) => { timer = setTimeout(() => reject(new Error('HOME_MODE_TIMEOUT')), 10000) })])
  } finally { if (timer) clearTimeout(timer) }
}
const valid = (data: any): data is Status => data && ['temporary', 'stores'].includes(data.mode) && Number.isSafeInteger(data.revision) && typeof data.can_change === 'boolean'

export function WebHomeControl () {
  const [status, setStatus] = useState<Status | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState('')
  const [target, setTarget] = useState<Mode | null>(null)
  async function read () {
    const { data, error: failure } = await bounded(supabaseClient.rpc('web_home_control_status'))
    if (failure || !valid(data)) throw new Error('No se ha podido comprobar el modo activo.')
    setStatus(data)
    return data
  }
  async function refresh () {
    setBusy(true); setError(''); setConfirmed(''); setStatus(null)
    try { await read() } catch { setError('No se ha podido comprobar el modo activo. Actualiza antes de cambiarlo.') } finally { setBusy(false) }
  }
  useEffect(() => { refresh() }, [])
  async function change () {
    if (!target || !status?.can_change || busy) return
    const selected = target
    const revision = status.revision
    setTarget(null); setBusy(true); setError(''); setConfirmed('')
    try {
      const { error: failure } = await bounded(supabaseClient.rpc('set_web_home_mode', { next_mode: selected, expected_revision: revision }))
      if (failure) throw failure
      const actual = await read()
      if (actual.mode !== selected) throw new Error('HOME_MODE_NOT_OBSERVED')
      setConfirmed(`${labels[selected]} confirmada. Las visitas abiertas se actualizan en unos 15 segundos; las nuevas, al cargar.`)
    } catch {
      setStatus(null)
      setError('No se ha confirmado el cambio. Puede haber cambiado el estado o fallado la conexión. Pulsa Actualizar estado antes de volver a intentarlo; no se repetirá automáticamente.')
    } finally { setBusy(false) }
  }
  return <Card title='Home web'>
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      <Typography.Text>Web controlada: {FRONT_MAIN_URL}</Typography.Text>
      <Alert type='info' message='Cambia la portada y la navegación de tiendas de la web. La app, el pedido personalizado y la compra del carrito se mantienen.' />
      <Typography.Title level={4}>Modo activo: {status ? labels[status.mode] : 'Sin comprobar'}</Typography.Title>
      {error && <Alert type='error' message={error} />}
      {confirmed && <Alert type='success' message={confirmed} />}
      {status && !status.can_change && <Alert type='warning' message='Tu cuenta no está autorizada para cambiar el modo de la web.' />}
      <Space wrap>
        <Button disabled={busy || !status?.can_change || status.mode === 'temporary'} onClick={() => setTarget('temporary')}>Activar home temporal</Button>
        <Button disabled={busy || !status?.can_change || status.mode === 'stores'} onClick={() => setTarget('stores')}>Activar home con tiendas</Button>
        <Button loading={busy} onClick={() => { refresh() }}>Actualizar estado</Button>
      </Space>
      <Typography.Paragraph>Temporal: tiendas desde la app. Con tiendas: recupera la portada anterior y la navegación integrada en la web. No requiere un nuevo despliegue.</Typography.Paragraph>
    </Space>
    <Modal title={target ? `Activar ${labels[target]}` : ''} visible={target !== null} onCancel={() => setTarget(null)} onOk={() => { change() }} okText='Cambiar modo' cancelText='Cancelar'>
      <p>El cambio se aplicará a {FRONT_MAIN_URL} y quedará registrado.</p>
      <p>{target === 'stores' ? 'Se habilitará de nuevo la navegación por las tiendas integradas. Confirma que están listas para recibir visitas.' : 'La web mostrará la home temporal y dirigirá la navegación de tiendas a la app.'}</p>
    </Modal>
  </Card>
}
